import type { ReactElement, ReactNode } from "react";
import {
	Details,
	type DetailsProps,
} from "@/components/Organisms/Details/Details";
import { cx } from "@/utils/cx";

export interface DrawerScrollBodyProps {
	/**
	 * Figma "Show Details". When true, render Details above content.
	 * @default false
	 */
	showDetails?: boolean;
	/**
	 * Figma "_PlaceholderContainer".
	 * When true and `children` is absent, render the empty placeholder region.
	 * @default true
	 */
	showPlaceholder?: boolean;
	/** Replace the default Details instance when `showDetails` is true. */
	details?: ReactNode;
	/** Props for the default `<Details />` when `details` is omitted. */
	detailsProps?: DetailsProps;
	/** Figma "Content" instance swap — custom body below Details. */
	children?: ReactNode;
	className?: string;
	"data-testid"?: string;
}

/**
 * Scrollable drawer body (Figma Scroll body). Tertiary background, flex-grow,
 * independent overflow. Optionally composes Details and/or custom content.
 */
export function ScrollBody({
	showDetails = false,
	showPlaceholder = true,
	details,
	detailsProps,
	children,
	className,
	"data-testid": dataTestId = "drawer-scroll-body",
}: DrawerScrollBodyProps): ReactElement {
	const showPlaceholderRegion = showPlaceholder && children == null;

	return (
		<div
			className={cx(
				"flex min-h-0 w-full flex-1 flex-col items-start overflow-y-auto bg-tertiary",
				className,
			)}
			data-testid={dataTestId}
		>
			{showDetails ? (details ?? <Details {...detailsProps} />) : null}
			{children}
			{showPlaceholderRegion ? (
				<div
					className="min-h-18 w-full flex-1"
					data-testid={`${dataTestId}-placeholder`}
					aria-hidden
				/>
			) : null}
		</div>
	);
}

ScrollBody.displayName = "Drawer.ScrollBody";
